import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import api from "../../services/api";
import { normalizeIcd10Code } from "../../utils/icd10Library";
import { searchIcd10, fetchIcd10Family, groupIcd10Results } from "../../utils/icd10Search";
import {
  flagNonBillableIcd10Results,
  hideNonBillableIcd10Results,
  loadIcd10ParentCodes,
} from "../../utils/codeOrdering/icd10Parents";
import "./icd10CodePicker.css";

const PANEL_MAX_HEIGHT = 380;
const EMPTY_RESULT = { groups: [], flat: [], root: "", error: false, hiddenNonBillable: 0 };

// One ICD-10 code picker for every surface: a pure dropdown where the only
// typing is the search box (multi-modal: E11.9 / e119 / hypertension / HTN /
// presion alta) and a code is only ever set by clicking an option. Renders the
// option list through a portal on document.body, so it can never be clipped by
// a scrollable ancestor (the bug that broke the inline table picker).
//
// variant="chip":   trigger is a code-chip button; the search box lives inside
//                   the popover. Used in the org library table.
// variant="inline": the search input IS the trigger (state-picker style). Used
//                   in the add-code modal.
function Icd10CodePicker({
  value = "",
  onPick,
  seedCode = "",
  icd10Library,
  variant = "chip",
  placeholder,
  triggerClassName = "",
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  resetOnPick = false,
  complianceNote = false,
  flagNonBillable = false,
}) {
  const { t } = useTranslation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [pos, setPos] = useState(null);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const requestIdRef = useRef(0);

  const setOpen = useCallback(
    (next) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      if (onOpenChange) onOpenChange(next);
    },
    [controlledOpen, onOpenChange]
  );

  const fetchIcd10 = useCallback((text) => api.getIcd10(text), []);

  const runQuery = useCallback(
    async (raw) => {
      const id = ++requestIdRef.current;
      const trimmed = String(raw || "").trim();
      let next = EMPTY_RESULT;
      if (trimmed.length >= 2) {
        setLoading(true);
        next = await searchIcd10({ query: trimmed, fetchIcd10, icd10Library });
      } else if (seedCode) {
        setLoading(true);
        next = await fetchIcd10Family({ code: seedCode, fetchIcd10, icd10Library });
      }
      // Non-billable handling happens HERE, per fetched result set — never at
      // render time. Deriving it from the async parent set during render meant
      // an open list could reshuffle under the user's cursor the moment the
      // code-split chunk resolved, and in a code-selection surface a misclick
      // is a wrong diagnosis code. Awaiting the memoized load instead costs one
      // await on the first search and keeps every rendered list immutable.
      // "hide" drops parent rows (a muted count line below the list says so);
      // any other truthy flag badges and demotes them instead. If the set
      // failed to load, both modes fail OPEN: everything renders as today.
      if (flagNonBillable && next.flat.length > 0) {
        const parentCodes = await loadIcd10ParentCodes();
        if (parentCodes) {
          if (flagNonBillable === "hide") {
            const kept = hideNonBillableIcd10Results(next.flat, parentCodes);
            if (kept !== next.flat) {
              next = {
                ...next,
                flat: kept,
                groups: groupIcd10Results(kept),
                hiddenNonBillable: next.flat.length - kept.length,
              };
            }
          } else {
            const flat = flagNonBillableIcd10Results(next.flat, parentCodes);
            if (flat !== next.flat) next = { ...next, flat, groups: groupIcd10Results(flat) };
          }
        }
      }
      if (id !== requestIdRef.current) return;
      setResult(next);
      setLoading(false);
      setFocusIndex(-1);
    },
    [fetchIcd10, flagNonBillable, icd10Library, seedCode]
  );

  const debouncedQuery = useDebouncedCallback(runQuery, 300);

  // Warm the code-split parent set as soon as a surface opts in, so the await
  // in runQuery is resolved by the time the user's first search lands. A load
  // failure resolves null (decoration skipped) and is retried on later calls.
  useEffect(() => {
    if (flagNonBillable) loadIcd10ParentCodes();
  }, [flagNonBillable]);

  // Opening resets the search and shows the seed code's family right away.
  // No stale-result invalidation needed on close: every open issues a fresh
  // runQuery, whose request-id check discards anything still in flight from
  // before (a late resolution while closed only touches hidden state).
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResult(EMPTY_RESULT);
    runQuery("");
    return () => debouncedQuery.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Portal positioning: below the trigger, flipped above when the viewport
  // space below is too tight, clamped to the viewport edges. Recomputed on any
  // ancestor scroll (capture) and on resize.
  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(Math.min(560, Math.max(380, rect.width)), vw - 16);
      const left = Math.min(Math.max(8, rect.left), vw - width - 8);
      const below = vh - rect.bottom;
      const flip = below < PANEL_MAX_HEIGHT && rect.top > below;
      // Floor of 140px: a trigger scrolled to the viewport edge would otherwise
      // produce a zero/negative maxHeight and an invisible panel.
      const cap = (space) => Math.max(140, Math.min(PANEL_MAX_HEIGHT, space));
      setPos(
        flip
          ? { left, width, bottom: vh - rect.top + 4, maxHeight: cap(rect.top - 8) }
          : { left, width, top: rect.bottom + 4, maxHeight: cap(below - 8) }
      );
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  // Outside-click close. Not input blur: blur fires before option mousedown in
  // a portal and would eat the pick.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (ev) => {
      if (panelRef.current?.contains(ev.target)) return;
      if (triggerRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, setOpen]);

  // Flat list of pickable rows (real group headers + options) for keyboard nav.
  const pickable = useMemo(() => {
    const rows = [];
    for (const group of result.groups) {
      if (group.header) rows.push(group.header);
      rows.push(...group.items);
    }
    return rows;
  }, [result]);

  const pick = (item) => {
    debouncedQuery.cancel();
    if (onPick) onPick(item);
    if (resetOnPick) setQuery("");
    setOpen(false);
  };

  const onSearchChange = (ev) => {
    const next = ev.target.value;
    setQuery(next);
    if (!open) setOpen(true);
    debouncedQuery(next);
  };

  const onSearchKeyDown = (ev) => {
    if (ev.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) return;
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, pickable.length - 1));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      if (pickable.length === 0) return;
      const focused = focusIndex >= 0 ? pickable[focusIndex] : null;
      const exact = pickable.find((it) => normalizeIcd10Code(it.n) === normalizeIcd10Code(query));
      pick(focused || exact || pickable[0]);
    }
  };

  // Keep the keyboard-focused row in view.
  const listRef = useRef(null);
  useEffect(() => {
    if (focusIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(".icd10-picker-option.is-focused");
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [focusIndex]);

  const optionRow = (item, extraClass) => {
    const isCurrent = value && normalizeIcd10Code(item.n) === normalizeIcd10Code(value);
    const isFocused = focusIndex >= 0 && pickable[focusIndex] === item;
    return (
      <button
        key={item.n}
        type="button"
        className={"icd10-picker-option" + (extraClass || "") + (isFocused ? " is-focused" : "")}
        onMouseDown={(ev) => {
          ev.preventDefault();
          pick(item);
        }}
      >
        <span className="icd10-picker-code-chip">{item.n}</span>
        <span className="icd10-picker-desc">
          {item.d}
          {item.via && <span className="icd10-picker-via"> · {t("common.icd10Picker.matchedVia", { term: item.via })}</span>}
        </span>
        {item.custom && <span className="icd10-picker-org-badge">{t("common.icd10Picker.orgBadge", "Org")}</span>}
        {item.nonBillable && (
          <span className="icd10-picker-nonbillable-badge">{t("common.icd10Picker.nonBillableBadge", "Non-billable")}</span>
        )}
        {isCurrent && <i className="notranslate material-icons icd10-picker-check">check</i>}
      </button>
    );
  };

  const isFamilyView = !query.trim() && !!result.root;
  const hasRows = result.groups.length > 0;

  const searchInput = (
    <input
      type="text"
      className={variant === "inline" ? "icd10-picker-inline-input" : "icd10-picker-search"}
      value={query}
      autoFocus={variant !== "inline"}
      onChange={onSearchChange}
      onKeyDown={onSearchKeyDown}
      onFocus={variant === "inline" ? () => setOpen(true) : undefined}
      onClick={variant === "inline" ? () => setOpen(true) : undefined}
      placeholder={placeholder || t("common.icd10Picker.searchPlaceholder")}
      disabled={disabled}
    />
  );

  const panel =
    open &&
    pos &&
    createPortal(
      <div
        ref={panelRef}
        className="icd10-picker-popover"
        style={{
          position: "fixed",
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          zIndex: 2001,
          ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
        }}
      >
        {variant !== "inline" && searchInput}
        <div className="icd10-picker-list" ref={listRef}>
          {isFamilyView && hasRows && (
            <div className="icd10-picker-section-label">{t("common.icd10Picker.familyHeader", { root: result.root })}</div>
          )}
          {result.groups.map((group) => (
            <div className="icd10-picker-group" key={group.root}>
              {group.header
                ? optionRow(group.header, " icd10-picker-group-header")
                : group.items.length > 1 && (
                    <div className="icd10-picker-section-label">
                      {group.root} · {t("profile.icd10Library.chapters." + group.chapterKey)}
                    </div>
                  )}
              {group.items.map((item) => optionRow(item))}
            </div>
          ))}
          {loading && <div className="icd10-picker-empty">{t("common.icd10Picker.loading")}</div>}
          {!loading && result.error && (
            <div className="icd10-picker-empty icd10-picker-error">{t("common.icd10Picker.searchError")}</div>
          )}
          {!loading && !result.error && !hasRows && (
            <div className="icd10-picker-empty">
              {query.trim().length >= 2 ? t("common.icd10Picker.noResults") : t("common.icd10Picker.typeToSearch")}
            </div>
          )}
          {!loading && result.hiddenNonBillable > 0 && (
            <div className="icd10-picker-hidden-note">
              {t("common.icd10Picker.nonBillableHidden", { count: result.hiddenNonBillable })}
            </div>
          )}
        </div>
        {complianceNote && <div className="icd10-picker-compliance">{t("common.icd10Picker.noteCompliance")}</div>}
      </div>,
      document.body
    );

  if (variant === "inline") {
    return (
      <div className="icd10-picker-inline" ref={triggerRef}>
        {searchInput}
        {panel}
      </div>
    );
  }

  if (variant === "icon") {
    // Minimal trigger for embedding next to existing row actions (the note's
    // pin/delete buttons): just the swap glyph, styled by the host's button
    // classes via triggerClassName.
    return (
      <>
        <button
          type="button"
          ref={triggerRef}
          className={triggerClassName || "small white no-border round"}
          onClick={() => setOpen(!open)}
          disabled={disabled}
          title={t("common.icd10Picker.swapHint", "Choose a different code")}
        >
          <i className="notranslate material-symbols-outlined">swap_horiz</i>
        </button>
        {panel}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={
          "icd10-code-chip icd10-chip-trigger" +
          (value ? "" : " icd10-chip-empty") +
          (triggerClassName ? " " + triggerClassName : "")
        }
        onClick={() => setOpen(!open)}
        disabled={disabled}
        title={t("common.icd10Picker.swapHint", "Choose a different code")}
      >
        {value || placeholder || t("common.icd10Picker.pickCode")}
        <i className="notranslate material-icons">expand_more</i>
      </button>
      {panel}
    </>
  );
}

export default Icd10CodePicker;
